import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import type { FolderTreeNode } from "../../types/project-storage.types";
import {
  collectAncestorIds,
  sortBySortOrder,
} from "../../utils/folder-tree";

type Props = {
  tree: FolderTreeNode[];
  selectedFolderId: string | null;
  folderCounts: Record<string, number>;
  onSelectFolder: (folderId: string) => void;
};

type FolderTreeItemProps = {
  node: FolderTreeNode;
  depth: number;
  selectedFolderId: string | null;
  expandedIds: Set<string>;
  folderCounts: Record<string, number>;
  onToggleExpand: (folderId: string) => void;
  onSelectFolder: (folderId: string) => void;
};

function FolderTreeItem({
  node,
  depth,
  selectedFolderId,
  expandedIds,
  folderCounts,
  onToggleExpand,
  onSelectFolder,
}: FolderTreeItemProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedFolderId === node.id;
  const count = folderCounts[node.id];

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelectFolder(node.id)}
        sx={{
          pl: 1.5 + depth * 1.5,
          py: 0.75,
          borderRadius: 1,
          mb: 0.25,
          "&.Mui-selected": {
            bgcolor: "#E8F2FB",
            "&:hover": { bgcolor: "#DCEAF8" },
          },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpand(node.id);
            }}
            sx={{ mr: 0.5, p: 0.25 }}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box width={28} flexShrink={0} />
        )}
        {isSelected || isExpanded ? (
          <FolderOpenIcon sx={{ fontSize: 18, color: "#1361A4", mr: 1 }} />
        ) : (
          <FolderIcon sx={{ fontSize: 18, color: "#6B7280", mr: 1 }} />
        )}
        <ListItemText
          primary={node.displayName}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? "#1361A4" : "#1F2937",
            noWrap: true,
          }}
        />
        {count > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 18,
              height: 14,
              px: 0.5,
              borderRadius: "4px",
              bgcolor: "#E5E7EB",
              color: "#4B5563",
              fontSize: 10,
              fontWeight: 600,
              lineHeight: "14px",
              textAlign: "center",
            }}
          >
            {count}
          </Box>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {sortBySortOrder(node.children).map((child) => (
              <FolderTreeItem
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedFolderId={selectedFolderId}
                expandedIds={expandedIds}
                folderCounts={folderCounts}
                onToggleExpand={onToggleExpand}
                onSelectFolder={onSelectFolder}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export function FolderTreeSidebar({
  tree,
  selectedFolderId,
  folderCounts,
  onSelectFolder,
}: Props) {
  const sortedTree = useMemo(() => sortBySortOrder(tree), [tree]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedFolderId || tree.length === 0) return;
    const ancestors = collectAncestorIds(tree, selectedFolderId);
    setExpandedIds((current) => {
      const next = new Set(current);
      ancestors.forEach((id) => next.add(id));
      if (selectedFolderId) next.add(selectedFolderId);
      return next;
    });
  }, [selectedFolderId, tree]);

  const handleToggleExpand = (folderId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  return (
    <Box
      width={300}
      flexShrink={0}
      borderRight="1px solid #E8E8E8"
      bgcolor="#FFFFFF"
      display="flex"
      flexDirection="column"
      minHeight={0}
    >
      <Box px={3} pt={3} pb={2}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#111827", fontSize: 18 }}
        >
          Pastas do projeto
        </Typography>
      </Box>
      <List
        sx={{
          px: 1.5,
          pb: 2,
          overflowY: "auto",
          flex: 1,
        }}
      >
        {sortedTree.map((node) => (
          <FolderTreeItem
            key={node.id}
            node={node}
            depth={0}
            selectedFolderId={selectedFolderId}
            expandedIds={expandedIds}
            folderCounts={folderCounts}
            onToggleExpand={handleToggleExpand}
            onSelectFolder={onSelectFolder}
          />
        ))}
      </List>
    </Box>
  );
}
